'use client'

import { Fragment, useState } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { ChevronRight, Coins, Loader2, Ticket, TriangleAlert, X } from 'lucide-react'
import { toast } from '@/lib/toast'
import { useAuth } from '@/lib/commerce/hooks'
import { convertPointsApi } from '@/lib/commerce/services/loyalty-api'
import { formatNumber, formatPrice } from '@/utils/util'

export default function ConvertVoucherModal({ open, setOpen, vendor, program, balance, onConverted }: { open: boolean, setOpen: (open: boolean) => void, vendor: any, program: any, balance: number, onConverted: () => void }) {
  const { session } = useAuth()

  const [convertPoints, setConvertPoints] = useState('')
  const [converting, setConverting] = useState(false)

  const convertValue = (Number(convertPoints) || 0) * (program?.pointValue || 1)
  const isSaveActive = Number(convertPoints) >= (program?.minPointsToConvert || 1) && Number(convertPoints) <= (balance || 0)

  const handleConvert = async () => {
    const points = Number(convertPoints)
    if (!points || points < (program?.minPointsToConvert || 1)) return toast.error(`Хамгийн багадаа ${formatNumber(program?.minPointsToConvert || 1)} оноо хөрвүүлнэ`)
    if (points > (balance || 0)) return toast.error('Онооны үлдэгдэл хүрэлцэхгүй байна')
    setConverting(true)
    const { data, error } = await convertPointsApi({ vendorId: vendor?.id, session: session || undefined }, points)
    setConverting(false)
    if (error) return toast.error(error)
    if (!data) return toast.error('Алдаа гарлаа. Дахин оролдоно уу')
    toast.success('Ваучер амжилттай үүслээ')
    setOpen(false)
    setConvertPoints('')
    onConverted()
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setOpen(false)}>
        <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/70" />
        </TransitionChild>
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <TransitionChild as={Fragment} enter="transition ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="transition ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
            <DialogPanel className="relative flex flex-col w-full sm:max-w-lg max-h-[90vh] rounded-t-3xl sm:rounded-3xl overflow-hidden" style={{ background: '#0b0b0e', border: '1px solid rgba(255,255,255,0.13)', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7)' }}>
              <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-4 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>Оноог ваучер болгох</div>
                  <p className="mt-1 text-[12px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>Цуглуулсан оноогоо ваучер болгон дараагийн захиалгадаа ашиглаарай</p>
                </div>
                <button type="button" onClick={() => setOpen(false)} aria-label="Хаах" className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-colors hover:bg-white/5 cursor-pointer shrink-0" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-dark px-6 py-5">
                <div className="space-y-4">
                  <div className="rounded-xl p-4 flex items-center justify-between gap-3" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="size-10 shrink-0 rounded-full grid place-items-center" style={{ background: 'white', color: '#0a0a0c' }}>
                        <Coins className="size-5" strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: '#a8a8b3' }}>Онооны үлдэгдэл</div>
                        <div className="text-lg font-bold tabular-nums leading-tight text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{formatNumber(balance || 0)}</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: '#a8a8b3' }}>Ханш</div>
                      <div className="text-sm font-semibold leading-tight text-white">1 оноо = {formatNumber(program?.pointValue || 1)}₮</div>
                    </div>
                  </div>

                  <div>
                    <label className="block">
                      <div className="block text-[10px] font-semibold uppercase tracking-[0.18em] mb-1.5" style={{ color: '#a8a8b3' }}>Хөрвүүлэх оноо</div>
                      <input type="number" value={convertPoints} onChange={(e: any) => setConvertPoints(e.target.value.replace(/[^0-9]/g, ''))} placeholder={String(program?.minPointsToConvert || 100)} className="w-full h-11 rounded-lg px-3.5 text-[13px] outline-none focus:ring-0 placeholder:text-white/30" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }} />
                    </label>
                    <div className="flex gap-2 mt-2">
                      {[25, 50, 100].map((percent) => {
                        const value = Math.floor(((balance || 0) * percent) / 100)
                        const selected = Number(convertPoints) === value && value > 0
                        return (
                          <button key={percent} type="button" disabled={value < 1} className="px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors disabled:opacity-40 cursor-pointer" style={selected ? { background: 'white', color: '#0a0a0c', border: '1px solid white' } : { background: 'transparent', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.15)' }} onClick={() => setConvertPoints(String(value))}>
                            {percent === 100 ? 'Бүгд' : `${percent}%`}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div className="rounded-xl p-4 flex items-center justify-center gap-4" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="text-center">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: '#a8a8b3' }}>Оноо</div>
                      <div className="font-semibold tabular-nums text-white mt-0.5">{formatNumber(Number(convertPoints) || 0)}</div>
                    </div>
                    <div className="size-8 rounded-full grid place-items-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}>
                      <ChevronRight className="size-4" strokeWidth={2.5} />
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: '#a8a8b3' }}>Ваучерын дүн</div>
                      <div className="text-xl font-bold tabular-nums text-white mt-0.5" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{formatPrice(convertValue)}</div>
                    </div>
                  </div>

                  <div className="rounded-xl p-3.5" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
                    <div className="flex items-center gap-2" style={{ color: '#fbbf24' }}>
                      <TriangleAlert className="size-4 shrink-0" strokeWidth={2} />
                      <span className="text-xs font-semibold">Хөрвүүлэхээсээ өмнө анхаараарай</span>
                    </div>
                    <ul className="mt-2 space-y-1.5 text-xs leading-relaxed list-disc pl-5" style={{ color: 'rgba(251,191,36,0.85)' }}>
                      <li>Ваучер болгосон оноог буцааж оноо болгох боломжгүй.</li>
                      {program?.voucherPartialUse ? (
                        <li>Ваучерын ашиглаагүй үлдэгдэл хадгалагдаж, дараагийн захиалгад ашиглагдана.</li>
                      ) : (
                        <li>Ваучер нэг захиалгад бүтнээрээ зарцуулагдах ба захиалгын дүн ваучераас бага бол зөрүү буцаан олгогдохгүй.</li>
                      )}
                      {program?.voucherExpireDays ? <li>Үүссэн ваучер {program.voucherExpireDays} хоногийн дотор хүчинтэй.</li> : null}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <button type="button" onClick={handleConvert} disabled={converting || !isSaveActive} className="w-full h-12 rounded-full text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed" style={{ background: 'white', color: '#0a0a0c' }}>
                  {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />}
                  Хөрвүүлэх
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
